import { Button, Drawer, Empty, Spin, Timeline } from "antd";
import { useQuery } from "@tanstack/react-query";
import medicalReviewApi from "@/services/medicalReviewApi";
import { formatFileSize } from "./medicalReview";
import { useMonitoringPlanDownload } from "./useMonitoringPlanDownload";

/**
 * Every version of the plan this trial has been reviewed against.
 *
 * Deliberately a drawer rather than a section: it answers an occasional
 * question ("what did the plan say when that record was reviewed?") and does
 * not deserve the same weight as the plan in force.
 */
export default function MonitoringPlanHistory({
  trialId,
  open,
  onClose,
}: {
  trialId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["medical-review-plan-history", trialId],
    queryFn: () => medicalReviewApi.planHistory(trialId),
    enabled: open,
  });
  const { download, downloadingVersion, contextHolder } =
    useMonitoringPlanDownload(trialId);

  const versions = data?.versions ?? [];

  return (
    <Drawer
      title="Monitoring plan history"
      placement="right"
      width={560}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {contextHolder}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spin />
        </div>
      ) : versions.length === 0 ? (
        <Empty description="No versions yet." className="my-12" />
      ) : (
        <Timeline
          items={versions.map((version) => ({
            key: version.id,
            children: (
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-[#2B4752]">
                  Version {version.version}
                  {version.plan_reference && (
                    <span className="ml-2 font-normal text-[#8FA5A0]">
                      {version.plan_reference}
                    </span>
                  )}
                </span>
                <span className="text-xs text-[#556C75]">
                  {version.authored_by || "Unknown author"} ·{" "}
                  {new Date(version.created_at).toLocaleString()}
                </span>
                {version.reason_for_change ? (
                  <span className="text-xs text-[#2B4752]">
                    {version.reason_for_change}
                  </span>
                ) : (
                  <span className="text-xs text-[#8FA5A0]">
                    First version — nothing to have changed from.
                  </span>
                )}
                {version.has_file && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-[#556C75]">
                      {version.original_filename}
                      {version.file_size_bytes
                        ? ` · ${formatFileSize(version.file_size_bytes)}`
                        : ""}
                    </span>
                    <Button
                      size="small"
                      type="link"
                      className="px-0"
                      loading={downloadingVersion === version.version}
                      onClick={() =>
                        download({
                          version: version.version,
                          filename: version.original_filename,
                        })
                      }
                    >
                      Download
                    </Button>
                  </div>
                )}
              </div>
            ),
          }))}
        />
      )}
    </Drawer>
  );
}
